import { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetEventQuery } from './eventsApi';
import { SectionNav } from './SectionNav';
import { Spinner } from '@mmef/shared/components';
import { Callout } from '@mmef/shared/components';
import { formatDate, formatLocation, formatTime } from '@mmef/shared/lib';
import { describeAgeRange } from '@mmef/shared/api';
import { EventLocationMap } from './EventLocationMap';
import { EventDescription } from './EventDescription';
import { eventDescription, eventTitle } from './eventMetadata';
import { usePageMetadata } from '@/app/usePageMetadata';
import { VerificationBadge } from '@mmef/shared/components';
import { SaveEventButton } from '@/components/SaveEventButton';
import { TicketList } from '@/components/TicketList';
import { messageOf, statusOf } from '@mmef/shared/api';
import { LEGAL } from '@/features/legal/legalDetails';

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const eventId = Number(id);
  const content = useRef<HTMLElement>(null);
  const { data: event, isLoading, isError, error } = useGetEventQuery(eventId, { skip: !Number.isFinite(eventId) });

  /*
   * Above the early returns because hooks have to run in the same order every render, and both
   * the loading and not-found branches return before this point. Undefined until the event
   * arrives, which leaves the site defaults in place — the right thing to show while loading, and
   * the right thing to leave on a page that turned out not to exist.
   */
  usePageMetadata({
    title: event && eventTitle(event),
    description: event && eventDescription(event),
  });

  if (isLoading) return <Spinner label="Loading event" />;

  if (isError || !event) {
    const gone = statusOf(error) === 404 || statusOf(error) === 410;
    return (
      <div className="page page--narrow">
        {/* Gone is the ordinary case, not a fault: every event is archived once it has passed, and
            an old link is the commonest way anybody arrives here. The server's own message names
            the id, which tells a reader nothing. Anything else — a network failure, a server
            error — still says what went wrong. */}
        <Callout tone={gone ? 'info' : 'error'} title={gone ? 'This event has finished' : "Couldn't load this event"}>
          {gone ? (
            'It has already taken place, or the organiser has taken it down. '
          ) : (
            <>{messageOf(error, 'Please try again in a moment.')} </>
          )}
          <Link to="/">See what&apos;s coming up</Link>
        </Callout>
      </div>
    );
  }

  const where = formatLocation([event.venueName, event.addressLine, event.city, event.postcode]);

  /*
   * One clause carried up to the booking button, when there is a note at the foot of the page.
   *
   * The full note belongs at the bottom — it is long, and a box explaining what we haven't checked
   * before the reader knows what the event is gets skipped. But a caveat below the only button on
   * the page is a caveat nobody who books has read, which is the one thing it exists to prevent.
   * So the short version travels to the decision and points back to the long one.
   *
   * Null when there is nothing at the foot to point at — an organiser who connected their own
   * account needs no caveat, and inventing one would undersell a listing that is better evidenced
   * than the rest.
   */
  const caveat = event.addedByUs
    ? "We haven't confirmed these details."
    : event.organiserVerification === 'UNVERIFIED'
      ? "Nobody has confirmed who runs this."
      : null;

  /*
   * The layout class only makes this a positioning context for the section rail, which hangs in
   * the margin at desktop widths and becomes a sticky row of chips below them. The article itself
   * is the same narrow centred column as every other page, and doesn't move when the rail appears.
   */
  return (
    <article className="page page--narrow event-layout" ref={content}>
      <SectionNav contentRef={content} watch={event.id} />

      <p className="breadcrumb">
        <Link to="/">← All events</Link>
      </p>

      {/* The title and the facts under it are one nav target: "Details" is also the way back to
          the top, and a heading is what a reader expects to land on. */}
      <div className="event-heading" id="details" data-section="Details">
        <h1>{event.title}</h1>
        <SaveEventButton eventId={event.id} />
      </div>

      {event.organiser && (
        <p className="lede">
          Organised by {event.organiser} <VerificationBadge tier={event.organiserVerification} />
        </p>
      )}


      <dl className="detail-list">
        <dt>Date</dt>
        <dd>
          <time dateTime={event.startTime}>{formatDate(event.startTime, event.timezone)}</time>
        </dd>

        <dt>Time</dt>
        <dd>
          {formatTime(event.startTime, event.timezone)}
          {event.endTime && ` – ${formatTime(event.endTime, event.timezone)}`}{' '}
          <span className="muted">({event.timezone})</span>
        </dd>

        {where && (
          <>
            <dt>Where</dt>
            <dd>
              {where}
              {/* Said right here rather than only in the note at the foot, because this is the
                  line somebody copies into their maps app. A caveat they'd meet after deciding
                  where to drive is a caveat that arrived too late to do anything. */}
              {event.addressResolved && (
                <p className="detail-list__caveat">
                  The organiser published the venue name but not its address, so we looked this up
                  ourselves — it may not be right, and the venue can change. Check on{' '}
                  {event.externalUrl ? (
                    <a href={event.externalUrl} target="_blank" rel="noopener noreferrer">
                      the organiser&apos;s own page
                    </a>
                  ) : (
                    "the organiser's own page"
                  )}{' '}
                  before travelling.
                </p>
              )}
            </dd>
          </>
        )}

        {/* Only when stated. Absence means the organiser said nothing about ages, and printing
            "all ages" would put words in their mouth about who is welcome. */}
        {event.ageRanges.length > 0 && (
          <>
            <dt>Ages</dt>
            <dd>{event.ageRanges.map(describeAgeRange).join(', ')}</dd>
          </>
        )}

        {/* No "Booking" row: it linked to the same place as the button below, and two links to
            one destination means neither reads as the thing to press. The host is named in the
            booking panel instead, where it sits beside the action it describes. */}
      </dl>

      {event.externalUrl && (
        <section className="booking-section" id="booking" data-section="Booking">
          <div className="booking-panel">
            <p className="booking-panel__label">Booking</p>
            {/* Outbound to an organiser's site: noopener/noreferrer. */}
            <a
              className="button button--primary"
              href={event.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Book on the organiser&apos;s site
            </a>
            <p className="booking-panel__note">
              {/* The page never used to say who takes the money, which for a directory that sells
                  nothing is the most important fact on it. */}
              Tickets are sold by the organiser at <strong>{hostOf(event.externalUrl)}</strong>, not
              by us.
              {caveat && (
                <>
                  {' '}
                  {caveat}{' '}
                  <a href="#listing-note">See the full note at the foot of this page.</a>
                </>
              )}
            </p>

            {/*
              A way to tell us the link is wrong.

              Nobody checks these links except us, and we get them wrong: several pointed at an
              organiser's whole events page rather than the event, and one pointed at a different
              event entirely. The nightly checker only sees HTTP status, so a link that loads the
              wrong page looks perfectly healthy to it. The reader who clicked through is the only
              one who actually knows, and until now had no way to say so.

              A mailto for the same reason the takedown route is one: a report that needs an account
              is a report nobody makes.
            */}
            <p className="booking-panel__report">
              Link broken or pointing at the wrong event?{' '}
              <a href={reportLinkMailto(event)}>Let us know</a>.
            </p>
          </div>
        </section>
      )}

      {event.description && (
        <section className="section" id="about" data-section="About this event">
          <h2>About this event</h2>
          <EventDescription description={event.description} />
        </section>
      )}

      {/* Above the map and the booking link on purpose: "what does it cost" is the question people
          ask immediately after "when is it", and answering it here means they reach the
          organiser's booking page already knowing the answer. */}
      <TicketList tickets={event.tickets} />

      <EventLocationMap event={event} />

      {/* The full note, last on the page and linked to from the booking panel.

          Two different situations, and saying the wrong one is the misleading bit: an event we
          added from a public listing was not "submitted to us" — the organiser never contacted us
          at all, and implying they did claims a relationship that doesn't exist. */}
      {event.addedByUs ? (
        <section className="section" id="listing-note" data-section="This listing">
          <Callout tone="info" title="We added this listing">
            <p>
              We found this event published elsewhere and listed it here. The organiser
              didn&apos;t send it to us and hasn&apos;t confirmed the details, so check their own
              site before booking or travelling, and meet in public places.
            </p>
            <p>
              <strong>Is this your event?</strong>{' '}
              <a href={claimMailto(event)}>Claim it or ask us to take it down</a> — we&apos;ll do
              either, no questions asked.
            </p>
          </Callout>
        </section>
      ) : (
        event.organiserVerification === 'UNVERIFIED' && (
          <section className="section" id="listing-note" data-section="This listing">
            <Callout tone="info" title="We haven’t confirmed this organiser">
              This listing was submitted to us and nobody has verified who runs it. Check the
              organiser’s own site before booking or travelling, and meet in public places.
            </Callout>
          </section>
        )
      )}
    </article>
  );
}

/**
 * A pre-addressed email for claiming a listing or asking for it to be removed.
 *
 * <p>A mailto rather than a form, because a takedown route that needs an account is not a takedown
 * route. An organiser who wants their event gone should be able to say so in one click, from
 * whatever address they already use, without signing up to the site that listed them without
 * asking.
 *
 * <p>The subject carries the event id so the message is actionable on its own — an operator
 * shouldn't have to reply asking which listing was meant.
 */
function claimMailto(event: { id: number; title: string }): string {
  const subject = `Event ${event.id}: ${event.title}`;
  const body = [
    'Please tell us which you would like:',
    '',
    '  - I run this event and would like to claim the listing',
    '  - Please remove this listing',
    '',
    'Anything else we should know:',
    '',
  ].join('\n');
  return `mailto:${LEGAL.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * A pre-addressed email for reporting a booking link that's wrong.
 *
 * <p>The link we send people to is the one thing on the page nothing verifies. The nightly checker
 * reads HTTP status, so a link that loads somebody's whole events index — or another event
 * altogether — comes back as healthy. Only the person who clicked it knows, and this is the first
 * route they've had to say so.
 *
 * <p>Carries the event id and the link itself, so a report is actionable without a reply asking
 * which listing and where it went.
 */
function reportLinkMailto(event: { id: number; title: string; externalUrl: string | null }): string {
  const subject = `Event ${event.id}: booking link problem — ${event.title}`;
  const body = [
    'What happened when you followed the booking link?',
    '',
    '  - It did not load at all',
    "  - It went to the organiser's site, but not to this event",
    '  - It went to a different event',
    '  - Something else:',
    '',
    `The link we sent you to: ${event.externalUrl ?? '(none recorded)'}`,
    '',
  ].join('\n');
  return `mailto:${LEGAL.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** "eventbrite.co.uk" — the domain alone, so people can see where a link goes before clicking. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
